import React, { useEffect, useRef, useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Link,
  Grid,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import BrandingLogo from "components/branding-logo";
import { useSite } from "hooks/use-site";
import { useAppSelector, useAppDispatch } from "store";
import { useNavigate } from "react-router-dom";
import { signUpWithReferral } from "service/api";
import { errorNotification } from "features/notifications/notificationsSlice";
import { setNewUser } from "features/register/registerSlice";
import ReCAPTCHA from "react-google-recaptcha";
import {
  AgreementDto,
  useGetAgreementsListQuery,
} from "../../features/site/siteApi";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { isValidE164 } from "utils/phoneE164";
import { useGetExcludedCountryCodesQuery } from "features/counteris/countriesApi";
import { yupPassword } from "utils/password";
import PasswordStrengthMeter from "components/password-strength-meter";
import PasswordField from "components/password-field";

type InitialValues = {
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
  [key: string]: any;
};

const SignUpWithPassword = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const newUser = useAppSelector((state) => state.register.newUser);
  const { strongPasswordEnabled } = useSite();
  const isBitcoSite = window.location.hostname.includes(
    "m-bitco.bitcohunters.com"
  );
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const agreementCheckedAtRef = useRef<Record<string, string>>({});

  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const { data } = useGetAgreementsListQuery();
  const agreements: AgreementDto[] = data?.agreements || [];
  const { data: excludedCodesData } = useGetExcludedCountryCodesQuery(
    { kycProgramId: newUser?.kyc_program_id! },
    { skip: !newUser?.kyc_program_id }
  );
  const excludedCountryCodes = (excludedCodesData?.excludedCountryCodes || [
    "IR",
  ]) as MuiTelInputCountry[];

  const baseValidation: Record<string, any> = {
    email: Yup.string()
      .email(t("auth.signup_with_password.validation.email_invalid"))
      .required(t("auth.signup_with_password.validation.email_required")),

    phone: Yup.string()
      .required(t("auth.signup_with_password.validation.phone_required"))
      .test(
        "e164",
        t("auth.signup_with_password.validation.phone_invalid"),
        (value) => isValidE164(value)
      ),

    password: yupPassword(t, {
      strong: strongPasswordEnabled,
      personalFields: ["email", "phone"],
    }),

    passwordConfirmation: Yup.string()
      .required(t("auth.signup_with_password.validation.password_match"))
      .oneOf(
        [Yup.ref("password"), null],
        t("auth.signup_with_password.validation.password_match")
      ),
  };

  agreements.forEach((a) => {
    const localized: { text?: string } =
      (a.translations && a.translations[currentLang]) ||
      (a.translations && a.translations["en"]) ||
      {};

    const text = localized.text || a.text || "";

    baseValidation[`agreement_${a.id}`] = Yup.boolean().oneOf(
      [true],
      t("auth.signup_with_password.validation.agreement_required", {
        text: text.slice(0, 30),
      })
    );
  });

  const validationSchema = Yup.object().shape(baseValidation);

  const initialValues: InitialValues = {
    email: "",
    phone: "",
    password: "",
    passwordConfirmation: "",
  };

  agreements.forEach((a) => {
    initialValues[`agreement_${a.id}`] = false;
  });

  if (!newUser) {
    navigate("/signin");
  } else {
    initialValues.email = newUser.email;
    initialValues.phone = newUser.phone;
  }

  const onSubmit = (
    formValues: InitialValues,
    { setSubmitting }: FormikHelpers<InitialValues>
  ) => {
    if (!newUser) return;
    const { email, phone, password } = formValues;
    setSubmitting(true);
    const formattedPhone = phone.replace("+", "00").replace(" ", "").trim();
    const form3_json = localStorage.getItem("form3_json");

    if (isBitcoSite && !recaptchaToken) {
      dispatch(
        errorNotification(
          t("auth.signup_with_password.errors.captcha_required")
        )
      );
      setSubmitting(false);
      return;
    }

    const agreement_consents: Record<string, string> = {};
    agreements.forEach((a) => {
      if (formValues[`agreement_${a.id}`] === true) {
        agreement_consents[String(a.id)] =
          agreementCheckedAtRef.current[`agreement_${a.id}`] ||
          new Date().toISOString();
      }
    });

    signUpWithReferral({
      ...newUser,
      email,
      password,
      phone: formattedPhone,
      form3_json,
      ui_language: currentLang,
      agreement_consents,
      ...(isBitcoSite && { recaptchaToken }),
    })
      .then(() => {
        dispatch(setNewUser({ ...newUser, email, phone }));
        localStorage.removeItem("form3_json");
        navigate("/registration-completed");
      })
      .catch((err) => {
        dispatch(
          errorNotification(
            err?.message ||
              t("auth.signup_with_password.errors.something_wrong")
          )
        );
      })
      .finally(() => setSubmitting(false));
  };

  const {
    handleSubmit,
    getFieldMeta,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    isSubmitting,
    values,
  } = useFormik<InitialValues>({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
  });

  const phoneMeta = getFieldMeta("phone");
  const showPhoneError =
    !!phoneMeta.error &&
    (phoneMeta.touched ||
      String(phoneMeta.value || "").replace(/\D/g, "").length > 3);

  const handlePhoneChange = (newValue: string) => {
    setFieldValue("phone", newValue, true);
  };

  const handlePhoneBlur = () => {
    setFieldTouched("phone", true, true);
  };

  const handleAgreementChange = (
    event: React.SyntheticEvent,
    checked: boolean
  ) => {
    const name = (event.target as HTMLInputElement).name;
    if (checked) {
      agreementCheckedAtRef.current[name] = new Date().toISOString();
    } else {
      delete agreementCheckedAtRef.current[name];
    }
    handleChange(event as React.ChangeEvent<HTMLInputElement>);
  };

  useEffect(() => {
    agreements.forEach((a) => {
      const key = `agreement_${a.id}`;
      if (!(key in values)) {
        setFieldValue(key, false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreements]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box paddingY={2.5} paddingX={4} sx={{ bgcolor: "brand.auth" }}>
              <BrandingLogo width={200} />
            </Box>
            <CardContent>
              <Stack spacing={4}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight={"600"}>
                    {t("auth.signup_with_password.title")}
                  </Typography>
                </Box>

                <Grid container rowSpacing={2} columnSpacing={0.5}>
                  <Grid xs={12} item>
                    <TextField
                      label={t("auth.signup_with_password.email")}
                      name="email"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("email").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("email").touched &&
                        !!getFieldMeta("email").error
                      }
                      helperText={
                        getFieldMeta("email").touched &&
                        getFieldMeta("email").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <PasswordField
                      label={t("auth.signup_with_password.password")}
                      name="password"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("password").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("password").touched &&
                        !!getFieldMeta("password").error
                      }
                      helperText={
                        getFieldMeta("password").touched &&
                        getFieldMeta("password").error
                      }
                    />
                    {strongPasswordEnabled && (
                      <PasswordStrengthMeter
                        password={String(getFieldMeta("password").value || "")}
                        personalTerms={[
                          String(getFieldMeta("email").value || ""),
                          String(getFieldMeta("phone").value || ""),
                        ]}
                      />
                    )}
                  </Grid>
                  <Grid xs={12} item>
                    <PasswordField
                      label={t("auth.signup_with_password.password_confirm")}
                      name="passwordConfirmation"
                      variant="outlined"
                      fullWidth
                      value={getFieldMeta("passwordConfirmation").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("passwordConfirmation").touched &&
                        !!getFieldMeta("passwordConfirmation").error
                      }
                      helperText={
                        getFieldMeta("passwordConfirmation").touched &&
                        getFieldMeta("passwordConfirmation").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <MuiTelInput
                      excludedCountries={excludedCountryCodes}
                      focusOnSelectCountry
                      forceCallingCode
                      fullWidth
                      defaultCountry={"CZ"}
                      label={t("auth.signup_with_password.mobile")}
                      name="phone"
                      value={phoneMeta.value}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      error={showPhoneError}
                      helperText={showPhoneError ? phoneMeta.error : undefined}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {t("auth.signup_with_password.mobile_hint")}
                    </Typography>
                  </Grid>
                </Grid>

                <Stack>
                  {agreements.map((agreement) => {
                    const key = `agreement_${agreement.id}`;

                    const localized: {
                      text?: string;
                      keyWords?: string | null;
                    } =
                      agreement.translations?.[currentLang] ??
                      agreement.translations?.["en"] ??
                      {};

                    const text: string = localized.text ?? agreement.text;
                    const keyWords: string | null =
                      localized.keyWords ?? agreement.keyWords;
                    const link = agreement.link;

                    const renderAgreementText = () => {
                      if (keyWords && link && text.includes(keyWords)) {
                        const parts = text.split(keyWords);
                        return (
                          <Typography
                            component="span"
                            sx={{ display: "inline", ml: 0.5 }}
                          >
                            {parts[0]}
                            <Link
                              underline="hover"
                              sx={{ mx: 0.3 }}
                              href={link}
                              target="_blank"
                            >
                              {keyWords}
                            </Link>
                            {parts.slice(1).join(keyWords)}
                          </Typography>
                        );
                      }

                      return (
                        <Typography component="span" sx={{ ml: 0.5 }}>
                          {text}
                        </Typography>
                      );
                    };

                    return (
                      <FormGroup key={agreement.id}>
                        <FormControlLabel
                          control={<Checkbox />}
                          label={
                            <Typography
                              sx={{ display: "flex" }}
                              component="span"
                            >
                              {renderAgreementText()}
                              <FormHelperText
                                sx={{ color: "error.main", ml: 1 }}
                              >
                                {getFieldMeta(key).touched &&
                                  getFieldMeta(key).error &&
                                  `(${getFieldMeta(key).error})`}
                              </FormHelperText>
                            </Typography>
                          }
                          name={key}
                          checked={getFieldMeta(key).value}
                          onChange={handleAgreementChange}
                          onBlur={handleBlur}
                        />
                      </FormGroup>
                    );
                  })}

                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={600}>
                        {t("auth.signup_with_password.notes.title")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ul style={{ paddingLeft: 20 }}>
                        <li>
                          <Typography variant="body2">
                            {t("auth.signup_with_password.notes.pep")}
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            {t("auth.signup_with_password.notes.corporate")}
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            {t("auth.signup_with_password.notes.personal")}
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            {t("auth.signup_with_password.notes.verification")}
                          </Typography>
                        </li>
                      </ul>
                    </AccordionDetails>
                  </Accordion>
                </Stack>

                {isBitcoSite && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <ReCAPTCHA
                      sitekey="6LdVQFUrAAAAAP5P440WaxAqfPVNhHzkqj9EGwqM"
                      onChange={(token) => setRecaptchaToken(token)}
                    />
                  </Box>
                )}

                <Stack spacing={2}>
                  <Box display="flex" gap={2} alignItems="center">
                    <LoadingButton
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {t("auth.signup_with_password.submit")}
                    </LoadingButton>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default SignUpWithPassword;
